import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { io, Socket } from 'socket.io-client';
import { logWebSocket } from '../utils/fileLogger';
import { setMenuItems, setCurrentCart, setCurrentOrder, setCurrentBooking, setCustomerInfo, clearWebSocketData } from '../store/slices/voiceSlice';
import { AppDispatch } from '../store/store';
import { MenuItem, OrderData, BookingData, CartData } from '../types/voice';

// Handle environment variable for both Vite and Webpack
const MCP_SERVER_URL = (typeof import.meta !== 'undefined' && import.meta.env?.VITE_MCP_SERVER_URL)
  || (typeof process !== 'undefined' && process.env?.REACT_APP_MCP_SERVER_URL)
  || 'https://vrestmcp.qastco.com';

interface MenuDisplayedEvent {
  event: 'menu_displayed';
  data: {
    category?: string;
    search_term?: string;
    items: MenuItem[];
    count: number;
  };
  session_id: string;
  timestamp: string;
}

interface OrderCreatedEvent {
  event: 'order_created';
  data: OrderData;
  session_id: string;
  timestamp: string;
}

interface CartUpdatedEvent {
  event: 'cart_updated';
  data: CartData;
  session_id: string;
  timestamp: string;
}

interface BookingCreatedEvent {
  event: 'booking_created';
  data: BookingData;
  session_id: string;
  timestamp: string;
}

interface EndCallEvent {
  event: 'end_call';
  data: {
    reason: string;
    message: string;
  };
  session_id: string;
  timestamp: string;
}

interface CustomerInfoUpdatedEvent {
  event: 'customer_info_updated';
  data: {
    name?: string;
    phone?: string;
    email?: string;
    address?: any;
  };
  session_id: string;
  timestamp: string;
}

interface WebSocketHookReturn {
  socket: Socket | null;
  isConnected: boolean;
  joinSession: (sessionId: string) => void;
  leaveSession: () => void;
  onEndCall: (callback: () => void) => void;
}

export const useWebSocket = (): WebSocketHookReturn => {
  const dispatch = useDispatch<AppDispatch>();
  const socketRef = useRef<Socket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const currentSessionRef = useRef<string | null>(null);
  const endCallCallbackRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    // Initialize socket connection
    logWebSocket.connecting(MCP_SERVER_URL);

    const socket = io(MCP_SERVER_URL, {
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: 5,
    });

    socketRef.current = socket;

    // Connection event handlers
    socket.on('connect', () => {
      logWebSocket.connected(socket.id || 'unknown', socket.io.engine.transport.name);
      setIsConnected(true);
    });

    socket.on('disconnect', (reason) => {
      logWebSocket.disconnected(reason);
      setIsConnected(false);
    });

    socket.on('connect_error', (error) => {
      logWebSocket.error(error);
      setIsConnected(false);
    });

    // Listen for menu_displayed events
    socket.on('menu_displayed', (event: MenuDisplayedEvent) => {
      console.log('🎯 menu_displayed EVENT RECEIVED:', {
        session: event.session_id,
        itemCount: event.data?.items?.length || 0,
        category: event.data?.category,
        searchTerm: event.data?.search_term
      });

      logWebSocket.menuDisplayed(
        event.session_id,
        event.data?.items?.length || 0,
        event.data?.category
      );

      if (event.data?.items) {
        console.log('📦 Setting menu items:', event.data.items.length, 'items');

        // SMART LOGIC:
        // - If items returned > 0: REPLACE the menu (AI is showing filtered results)
        // - If items returned = 0: KEEP existing menu (search failed, don't clear)
        if (event.data.items.length > 0) {
          // Replace menu with new filtered items - dispatch to Redux
          dispatch(setMenuItems(event.data.items));
          console.log('✅ Menu REPLACED with', event.data.items.length, 'items');
        } else {
          console.log('⚠️ Empty items array - keeping existing menu (search returned nothing)');
        }
      } else {
        console.log('❌ No items data in event');
      }
    });

    // Listen for cart_updated events
    socket.on('cart_updated', (event: CartUpdatedEvent) => {
      console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      console.log('🛒 CART_UPDATED EVENT RECEIVED!');
      console.log('   Session ID:', event.session_id);
      console.log('   Item Count:', event.data?.itemCount);
      console.log('   Total:', event.data?.total);
      console.log('   Items:', event.data?.items);
      console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');

      if (event.data) {
        console.log('📦 Dispatching setCurrentCart with data:', event.data);
        dispatch(setCurrentCart(event.data));
        console.log('✅ Cart state should be updated now');
      } else {
        console.error('❌ No data in cart_updated event!');
      }
    });

    // Listen for order_created events
    socket.on('order_created', (event: OrderCreatedEvent) => {
      console.log('🛒 ORDER_CREATED EVENT:', {
        orderNumber: event.data?.orderNumber,
        total: event.data?.total,
        items: event.data?.items,
        itemsCount: event.data?.items?.length
      });

      logWebSocket.orderCreated(
        event.session_id,
        event.data?.orderNumber || 'unknown',
        event.data?.total || 0
      );

      if (event.data) {
        console.log('💰 Order items with prices:', event.data.items?.map((item: any) => ({
          name: item.itemName,
          qty: item.quantity,
          rate: item.rate,
          total: (item.quantity || 1) * (item.rate || 0)
        })));
        dispatch(setCurrentOrder(event.data));
      }
    });

    // Listen for booking_created events
    socket.on('booking_created', (event: BookingCreatedEvent) => {
      console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      console.log('📅 BOOKING CREATED');
      console.log(`   Booking Number: ${event.data?.bookingNumber}`);
      console.log(`   Customer: ${event.data?.customerName}`);
      console.log(`   Date: ${event.data?.bookingDate} at ${event.data?.bookingTime}`);
      console.log(`   Guests: ${event.data?.numberOfGuests}`);
      console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      if (event.data) {
        dispatch(setCurrentBooking(event.data));
      }
    });

    // Listen for customer_info_updated events
    socket.on('customer_info_updated', (event: CustomerInfoUpdatedEvent) => {
      console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      console.log('👤 CUSTOMER INFO UPDATED EVENT RECEIVED!');
      console.log('   Full event object:', event);
      console.log(`   Session ID: ${event.session_id}`);
      console.log('   Event data:', event.data);
      console.log(`   Name: ${event.data?.name || 'N/A'}`);
      console.log(`   Phone: ${event.data?.phone || 'N/A'}`);
      console.log(`   Email: ${event.data?.email || 'N/A'}`);
      console.log(`   Address: ${event.data?.address ? JSON.stringify(event.data.address) : 'N/A'}`);
      console.log('   Has event.data?', !!event.data);
      console.log('   Dispatching setCustomerInfo with:', event.data);
      console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');

      if (event.data) {
        dispatch(setCustomerInfo(event.data));
        console.log('✅ DISPATCHED setCustomerInfo action');
      } else {
        console.warn('⚠️ No event.data - NOT dispatching setCustomerInfo');
      }
    });

    // Listen for end_call events
    socket.on('end_call', (event: EndCallEvent) => {
      console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      console.log('📞 END CALL EVENT RECEIVED');
      console.log(`   Reason: ${event.data?.reason}`);
      console.log(`   Message: ${event.data?.message}`);
      console.log(`   Session: ${event.session_id}`);
      console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');

      // Call the registered callback to end the Ultravox call
      if (endCallCallbackRef.current) {
        console.log('🔚 Executing end call callback');
        endCallCallbackRef.current();
      } else {
        console.warn('⚠️ No end call callback registered');
      }
    });

    // Listen for session joined confirmation
    socket.on('joined-session', (data: { success: boolean; sessionId: string; message: string }) => {
      logWebSocket.joinedSession(data.sessionId);
    });

    // Cleanup on unmount
    return () => {
      console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      console.log('🔌 WEBSOCKET: Disconnecting...');
      console.log('   Timestamp:', new Date().toISOString());
      console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      socket.disconnect();
    };
  }, [dispatch]);

  const joinSession = (sessionId: string) => {
    if (!socketRef.current) {
      console.error('❌ Cannot join session - socket is null');
      return;
    }
    if (!sessionId) {
      console.error('❌ Cannot join session - sessionId is empty');
      return;
    }

    // Clear previous session data when starting a new session
    console.log('🧹 Clearing previous session data before joining new session');
    dispatch(clearWebSocketData());

    logWebSocket.joiningSession(sessionId);
    currentSessionRef.current = sessionId;
    console.log('📤 Emitting join-session event with sessionId:', sessionId);
    socketRef.current.emit('join-session', sessionId);
    console.log('✅ join-session event emitted successfully');
  };

  const leaveSession = () => {
    if (currentSessionRef.current) {
      console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      console.log('👋 WEBSOCKET: Leaving session');
      console.log('   Session ID:', currentSessionRef.current);
      console.log('   Timestamp:', new Date().toISOString());
      console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');

      currentSessionRef.current = null;
      dispatch(clearWebSocketData());
    }
  };

  const onEndCall = (callback: () => void) => {
    console.log('📝 Registering end call callback');
    endCallCallbackRef.current = callback;
  };

  return {
    socket: socketRef.current,
    isConnected,
    joinSession,
    leaveSession,
    onEndCall,
  };
};
